import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, deleteCustomer } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Edit2, Trash2, Plus, Search, MoreHorizontal, Phone, Mail, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Updated Customer interface to include cpf
interface Customer {
  id: string;
  name: string;
  phone: string;
  cpf?: string; // Added CPF field
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  customer_type?: string;
  created_at: string;
  orders_count?: number;
  total_spent?: number;
}

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const AdminCustomersPage = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  // --- Realtime Subscription Effect --- 
  useEffect(() => {
    // Initial load
    loadCustomers();
    
    const channel = supabase
      .channel('customers-changes')
      .on(
        'postgres_changes',
        {
          event: '*', 
          schema: 'public',
          table: 'customers',
        },
        (payload) => {
          console.log('Realtime change detected in customers:', payload);
          // Simple approach: Just reload the current view
          // Debounce this if it causes too many reloads
          loadCustomers(searchTerm); 
        }
      )
      .subscribe();

    // Cleanup
    return () => {
      supabase.removeChannel(channel);
    };
  }, [searchTerm]); // Reload listener if search term changes (optional)

  // --- Load Customers Function --- 
  const loadCustomers = async (search?: string) => {
    if (!isRefreshing) setIsLoading(true); 
    try {
      console.log(`Loading customers with search term: "${search || ''}"`);
      const data = await fetchCustomers(search);
      console.log(`Loaded ${data.length} customers.`);
      setCustomers(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar lista de clientes', { duration: 2000 });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadCustomers(searchTerm);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadCustomers(searchTerm);
  };

  const handleEdit = (customerId: string) => {
    navigate(`/admin/customers/edit/${customerId}`);
  };

  // --- Delete Handler (Corrected Logic) --- 
  const handleDelete = async (customerId: string) => {
    // Double-check confirmation message regarding associated orders
    if (window.confirm('Tem certeza que deseja excluir este cliente? Verifique se ele não possui pedidos associados, pois a exclusão pode falhar devido a restrições.')) {
      try {
        // Attempt to delete from the database
        await deleteCustomer(customerId); 
        
        // **If deleteCustomer doesn't throw an error, then it succeeded**
        toast.success('Cliente excluído com sucesso', { duration: 1500 });
        
        // Update the UI state immediately ONLY after successful DB operation
        setCustomers(prevCustomers => prevCustomers.filter(c => c.id !== customerId));
        
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        // Check for specific foreign key violation error (adjust if needed based on actual error)
        // Corrected line 140: removed stray backtick
        if (error instanceof Error && (error.message.includes('violates foreign key constraint') || error.message.includes('constraint failed'))) { 
             toast.error('Erro: Não é possível excluir cliente com pedidos associados.', { duration: 3000 });
        } else {
             // Generic error for other issues (like RLS permission denied)
             toast.error(`Erro ao excluir cliente: ${error instanceof Error ? error.message : 'Erro desconhecido'}`, { duration: 2500 });
        }
      }
    }
  };

  // --- JSX Structure (remains the same) --- 
  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      {/* Header: Title, Search, Refresh, Add */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 sm:space-x-2">
        <h2 className="text-2xl font-bold">Clientes</h2>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline"
            size="icon"
            onClick={handleRefresh} 
            className="h-9 w-9" // Adjusted size
            disabled={isLoading || isRefreshing}
            title="Atualizar lista"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <form onSubmit={handleSearch} className="flex flex-grow sm:flex-grow-0 sm:w-64">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar (Nome, CPF, Tel, Email)..."
              className="rounded-r-none h-9"
            />
            <Button type="submit" className="rounded-l-none h-9">
              <Search className="h-4 w-4" />
            </Button>
          </form>
          <Button onClick={() => navigate('/admin/customers/new')} className="h-9">
            <Plus className="mr-2 h-4 w-4" /> Novo
          </Button>
        </div>
      </div>

      {/* Customer List Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4 px-6">
          <CardTitle className="text-lg">Lista de Clientes</CardTitle>
          <div className="text-sm text-muted-foreground">
            {isLoading ? 'Carregando...' : `${customers.length} cliente${customers.length !== 1 ? 's' : ''}`}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && customers.length === 0 ? ( // Show loader only on initial load
            <div className="py-12 text-center">Carregando clientes...</div>
          ) : !isLoading && customers.length === 0 ? ( // Show empty state after load
            <div className="py-12 text-center">
              {searchTerm ? 'Nenhum cliente encontrado para esta busca.' : 'Nenhum cliente cadastrado.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>CPF/CNPJ</TableHead> 
                  <TableHead>Contato</TableHead>
                  <TableHead>Pedidos</TableHead>
                  <TableHead>Total Gasto</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium py-3 px-6">{customer.name}</TableCell>
                    <TableCell className="py-3 px-6">{customer.cpf || '-'}</TableCell> 
                    <TableCell className="py-3 px-6">
                      <div className="flex flex-col space-y-0.5">
                        <span className="flex items-center text-sm">
                          <Phone className="h-3.5 w-3.5 mr-1.5 text-gray-500" /> {customer.phone}
                        </span>
                        {customer.email && (
                          <span className="flex items-center text-xs text-muted-foreground">
                            <Mail className="h-3.5 w-3.5 mr-1.5 text-gray-500" /> {customer.email}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 px-6 text-center">{customer.orders_count ?? 0}</TableCell>
                    <TableCell className="py-3 px-6">{formatCurrency(customer.total_spent)}</TableCell>
                    <TableCell className="text-right py-3 pr-6">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                           <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ações</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEdit(customer.id)}>
                            <Edit2 className="mr-2 h-4 w-4" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(customer.id)} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                            <Trash2 className="mr-2 h-4 w-4" /> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCustomersPage;
